import React from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/** Drag handle icon — accessible, touch-friendly */
export const DragHandle = ({ listeners, attributes }) => (
    <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        className="touch-none select-none cursor-grab active:cursor-grabbing px-2 py-3 text-[#F5F2EB]/40 hover:text-[#D4A437]"
    >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
        </svg>
    </button>
);

const SortableRow = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : "auto",
        opacity: isDragging ? 0.9 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} data-testid={`sortable-${id}`}>
            {children({ listeners, attributes })}
        </div>
    );
};

/**
 * SortableList
 * @param items   array of items with `id` field
 * @param onReorder  (newOrderedItems, newIds) => void
 * @param renderItem (item, dragProps) => ReactNode
 */
export const SortableList = ({ items, onReorder, renderItem }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = items.findIndex((i) => i.id === active.id);
        const newIdx = items.findIndex((i) => i.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const next = arrayMove(items, oldIdx, newIdx);
        onReorder(next, next.map((x) => x.id));
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                    {items.map((it) => (
                        <SortableRow key={it.id} id={it.id}>
                            {(dragProps) => renderItem(it, dragProps)}
                        </SortableRow>
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};
